import { useSprings } from '@react-spring/core';
import { animated } from '@react-spring/web';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useResizeObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';
import { sluggify } from '../../lib/utils';

// import Background from '../partials/SectionPageBackground';
import { useFloatingVideo } from '../../contexts/VideoContext';
import Blocks from '../blocks/Blocks';
import RoundedButton from '../buttons/Rounded';
import QuebecFrance from '../icons/bg-frames/QuebecFrance';

import styles from '../../../styles/pages/about.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    pathname: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    pathname: null,
    className: null,
};

const AboutPage = ({ page, pathname, className }) => {
    const { aboutSections } = page || {};
    const { setFloatingVideo } = useFloatingVideo();

    useEffect(() => {
        setFloatingVideo(null);
    }, [setFloatingVideo]);

    const slice = useMemo(() => {
        const slices = (pathname || '').split('/').filter((s) => !isEmpty(s));
        const [locale = null, parent = null, slug = null] = slices || []; // eslint-disable-line no-unused-vars
        return slug;
    }, [pathname]);

    useEffect(() => {
        let timeout = null;
        const element = document.getElementById(slice) || null;
        if (element !== null) {
            timeout = setTimeout(() => {
                const y = element.getBoundingClientRect().top + window.scrollY;
                window.scroll({
                    top: y,
                });
            }, 300);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [slice]);

    const slugs = useMemo(
        () => (aboutSections || []).map(({ title }) => sluggify(title)),
        [aboutSections],
    );

    const {
        ref,
        entry: { contentRect = null },
    } = useResizeObserver();

    const sectionsRef = useRef([]);

    const { height = 0 } = contentRect || {};
    const sectionsHeights = useMemo(
        () =>
            (sectionsRef.current || []).map((el) => {
                const rect = el.getBoundingClientRect();
                return rect.y + rect.height + window.scrollY;
            }),
        [height],
    );

    const getClipPath = useCallback(
        (index) => {
            const scrollValue = window.scrollY + window.innerHeight;
            let position = 0;

            sectionsHeights.forEach((sectionHeight, i) => {
                if (scrollValue > sectionHeight && i < sectionsHeights.length - 1) {
                    position =
                        1 +
                        i +
                        (scrollValue - sectionHeight) / (sectionsHeights[i + 1] - sectionHeight);
                    position += i * 0.3;
                }
            });
            const scale = 500;
            const value = Math.max(0, scale - (index / position) * scale);
            return {
                clipPath:
                    index === 0 || value > scale / 4
                        ? null
                        : `circle(${index === 0 ? scale : value}vh at 50% 20%)`,
            };
        },
        [sectionsHeights],
    );

    // eslint-disable-next-line no-unused-vars
    const [springs, api] = useSprings((aboutSections || []).length, getClipPath);

    useEffect(() => {
        const onScroll = () => {
            api.start(getClipPath);
        };
        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [sectionsHeights]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            ref={ref}
        >
            {/* <Background className={styles.header} pageHandle="media" /> */}
            <div className={styles.sections}>
                {(aboutSections || []).map((section, itemIndex) => {
                    const { blocks, links = null } = section;
                    const name = slugs[itemIndex];
                    return (
                        <section key={`section-${name}`} className={styles.section}>
                            <animated.div
                                id={name}
                                key={`section-${name}`}
                                ref={(el) => {
                                    sectionsRef.current[itemIndex] = el;
                                }}
                                // style={springs[itemIndex]}
                            >
                                {name === 'international' ? (
                                    <QuebecFrance className={styles.international} />
                                ) : null}
                                <Blocks
                                    className={styles.blocks}
                                    blocks={blocks}
                                    isContainer
                                    firstInPage
                                    blockClassName={styles.block}
                                    typeClassName={{
                                        title: styles.title,
                                        embed: styles.embedBlock,
                                        section: styles.sectionBlock,
                                        micromag: styles.micromagBlock,
                                    }}
                                />
                                {(links || []).length > 0 ? (
                                    <div className={styles.links}>
                                        {(links || []).map(
                                            ({ external, url, label }, linkIndex) => (
                                                <RoundedButton
                                                    key={`link-${linkIndex + 1}`}
                                                    className={styles.link}
                                                    href={url}
                                                    external={external}
                                                >
                                                    {label}
                                                </RoundedButton>
                                            ),
                                        )}
                                    </div>
                                ) : null}
                            </animated.div>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};
AboutPage.propTypes = propTypes;
AboutPage.defaultProps = defaultProps;

export default AboutPage;
